import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Phone, Sparkles, Leaf, Utensils, Instagram, Facebook } from "lucide-react";
import { api } from "@/lib/api";
import { usePhotos } from "@/lib/usePhotos";
import PackageCard from "@/components/PackageCard";
import NewsletterSection from "@/components/NewsletterSection";

const DEFAULT_HERO =
    "https://images.unsplash.com/photo-1559339352-11d035aa65de?auto=format&fit=crop&w=2880&q=90";

const INSTAGRAM_URL = "https://instagram.com/coralbay_catering";
const FACEBOOK_URL = "https://www.facebook.com/share/1FjP6AiPPj/?mibextid=wwXIfr";
const SUBSTACK_URL =
    "https://substack.com/@coralbaycatering?r=1wil5o&utm_medium=ios&utm_source=stories&shareImageVariant=blur";

function SubstackIcon({ className = "w-5 h-5" }) {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
            <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
        </svg>
    );
}

export default function HomePage() {
    const [packages, setPackages] = useState([]);
    const photos = usePhotos();

    useEffect(() => {
        api.get("/packages")
            .then((r) => setPackages(r.data))
            .catch(() => {});
    }, []);

    const heroImg = photos.hero || DEFAULT_HERO;

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[90vh] w-full overflow-hidden grain">
                <img
                    src={heroImg}
                    alt="Coral Bay Catering"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-lagoon-900/85 via-palm-700/55 to-coral-500/50" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-44 lg:pt-56 pb-32 lg:pb-40 text-sand-100">
                    {/* BRAND MARK — bright, crisp, very noticeable */}
                    <div className="mb-10" data-testid="hero-brand-mark">
                        <img
                            src="/coral-bay-logo-light.png"
                            alt="Coral Bay Catering"
                            className="w-72 sm:w-80 lg:w-96 h-auto [filter:drop-shadow(0_4px_24px_rgba(0,0,0,0.35))]"
                        />
                    </div>
                    <span
                        className="overline !text-sand-100/80"
                        data-testid="hero-overline"
                    >
                        St. John · U.S. Virgin Islands · Since 2002
                    </span>
                    <h1
                        className="font-serif font-light text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-6 max-w-4xl leading-[1.05]"
                        data-testid="hero-headline"
                    >
                        Fresh. Local. <em className="italic text-[#FA8072]">Innovative.</em>
                        <br />
                        Made with love.
                    </h1>
                    <p
                        className="mt-8 max-w-xl text-base lg:text-lg text-sand-100/80 leading-relaxed"
                        data-testid="hero-subtext"
                    >
                        Coral Bay Catering creates unforgettable culinary experiences
                        across the Caribbean — from intimate villa dinners to
                        celebrations under the palms. Reserve your date online with
                        a 50% deposit.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <Link
                            to="/book"
                            data-testid="hero-reserve-btn"
                            className="inline-flex items-center justify-center gap-2 bg-sand-100 text-forest-900 px-8 py-4 rounded-sm hover:bg-white transition-colors text-sm tracking-wide"
                        >
                            Reserve Your Date
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </Link>
                        <Link
                            to="/packages"
                            data-testid="hero-packages-btn"
                            className="inline-flex items-center justify-center gap-2 bg-transparent border border-sand-100/60 text-sand-100 px-8 py-4 rounded-sm hover:bg-sand-100/10 transition-colors text-sm tracking-wide"
                        >
                            Explore Menus
                        </Link>
                    </div>
                </div>

                {/* Side detail */}
                <div className="hidden lg:flex absolute bottom-10 right-10 z-10 flex-col items-end gap-3 text-sand-100/80">
                    <span className="overline !text-sand-100/60">Speak with us</span>
                    <a
                        href="tel:3404236611"
                        className="font-serif text-3xl tracking-tight hover:text-white transition-colors"
                    >
                        340.423.6611
                    </a>
                </div>
            </section>

            {/* VALUE PROPS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <span className="overline">What motivates us</span>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-forest-700 mt-5 leading-[1.05]">
                            We don't just cater food. <em>We cultivate experiences.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 space-y-8 text-forest-700/75 leading-relaxed">
                        <p>
                            Coral Bay Catering started with humble beginnings — the
                            dream to serve the St. John community delectable cuisine
                            created with heart using locally sourced fresh ingredients.
                        </p>
                        <p>
                            With over twenty-three years of experience catering in the
                            Virgin Islands, we make recommendations for every facet of
                            your event including flowers, decor, music, entertainment,
                            transportation, valet, tents and rental needs.
                        </p>
                        <div className="grid grid-cols-3 gap-6 pt-6 border-t border-palm-400/30">
                            {[
                                { icon: Leaf, label: "Locally Sourced" },
                                { icon: Utensils, label: "Custom Menus" },
                                { icon: Sparkles, label: "Full Service" },
                            ].map((f) => (
                                <div
                                    key={f.label}
                                    data-testid={`prop-${f.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="flex flex-col gap-2"
                                >
                                    <f.icon
                                        className="w-5 h-5 text-forest-400"
                                        strokeWidth={1.5}
                                    />
                                    <span className="text-sm text-forest-700">
                                        {f.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* PACKAGES PREVIEW */}
            <section className="bg-sand-200/60 py-24 lg:py-32" data-testid="home-packages-section">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
                        <div>
                            <span className="overline">Our Menus</span>
                            <h2 className="font-serif text-4xl md:text-5xl text-forest-700 mt-4 leading-tight">
                                Two drop-off menus.
                                <br />
                                Four on-site chef experiences.
                            </h2>
                        </div>
                        <Link
                            to="/packages"
                            data-testid="home-view-all-packages"
                            className="inline-flex items-center gap-2 text-forest-700 underline-link self-start md:self-end"
                        >
                            View all packages
                            <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                        </Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {packages.slice(0, 6).map((p, i) => (
                            <PackageCard key={p.id} pkg={p} index={i} />
                        ))}
                    </div>
                </div>
            </section>

            {/* POLICY STRIP */}
            <section className="gradient-sunset text-white py-16">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
                    <span className="overline !text-white/90 !font-bold">Booking Policy</span>
                    <p className="font-serif text-2xl md:text-3xl mt-5 leading-relaxed italic">
                        "Your reservation is confirmed once we've collected a 50%
                        deposit. Submit your date online and we'll call you back
                        with your invoice."
                    </p>
                    <p className="text-white/90 text-sm mt-6 tracking-wide">
                        20% Gratuity · 5% Processing · $65 Delivery (Drop-Offs) · $150
                        Server Fee (In-Villa, 6+ Guests) · $150 Holiday Fee (Dec 18 – Jan 4)
                    </p>
                    <p className="text-white/80 text-xs mt-3 tracking-wide">
                        Christmas Day & New Year's Eve are email-only bookings ($350
                        fee). Please contact us directly.
                    </p>
                    <Link
                        to="/book"
                        data-testid="policy-reserve-btn"
                        className="inline-flex items-center gap-2 mt-8 bg-white text-coral-500 px-8 py-4 rounded-sm hover:bg-sand-100 transition-colors text-sm tracking-wide font-semibold"
                    >
                        Begin Reservation
                        <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                </div>
            </section>

            {/* GALLERY (only if photos uploaded) */}
            {photos.gallery.length > 0 && (
                <section
                    className="bg-sand-100 py-24 lg:py-32"
                    data-testid="home-gallery-section"
                >
                    <div className="max-w-7xl mx-auto px-6 lg:px-10">
                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
                            <div>
                                <span className="overline">From our kitchen</span>
                                <h2 className="font-serif text-4xl md:text-5xl text-forest-700 mt-4 leading-tight">
                                    Moments, plated.
                                </h2>
                            </div>
                            <a
                                href="https://instagram.com/coralbay_catering"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="gallery-instagram-link"
                                className="inline-flex items-center gap-2 text-forest-700 underline-link self-start md:self-end"
                            >
                                Follow @coralbay_catering
                                <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                            </a>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {photos.gallery.slice(0, 12).map((g, i) => (
                                <div
                                    key={g.id}
                                    className="aspect-square overflow-hidden rounded-sm bg-sand-200 animate-fade-up"
                                    style={{ animationDelay: `${i * 60}ms` }}
                                    data-testid={`gallery-tile-${g.id}`}
                                >
                                    <img
                                        src={g.url}
                                        alt={g.alt || "Coral Bay Catering"}
                                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                                        loading="lazy"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FOLLOW US STRIP */}
            <section
                className="bg-sand-200/60 py-20 lg:py-24 border-y border-palm-400/20"
                data-testid="follow-us-strip"
            >
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                        <div className="lg:col-span-5">
                            <span className="overline">Follow along</span>
                            <h2 className="font-serif text-4xl md:text-5xl text-forest-700 mt-4 leading-[1.05]">
                                Stay close to the <em className="italic">island kitchen.</em>
                            </h2>
                            <p className="text-forest-700/70 leading-relaxed mt-5 max-w-md">
                                New menus, fresh catches, behind-the-scenes from St.
                                John — find us on the channels you already love.
                            </p>
                        </div>
                        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            <SocialCard
                                href={INSTAGRAM_URL}
                                testid="follow-instagram"
                                icon={<Instagram className="w-5 h-5" strokeWidth={1.5} />}
                                platform="Instagram"
                                handle="@coralbay_catering"
                                hint="Daily plates & stories"
                                accent="from-coral-500 to-coral-700"
                            />
                            <SocialCard
                                href={FACEBOOK_URL}
                                testid="follow-facebook"
                                icon={<Facebook className="w-5 h-5" strokeWidth={1.5} />}
                                platform="Facebook"
                                handle="Coral Bay Catering"
                                hint="Events & community"
                                accent="from-lagoon-500 to-lagoon-700"
                            />
                            <SocialCard
                                href={SUBSTACK_URL}
                                testid="follow-substack"
                                icon={<SubstackIcon className="w-5 h-5" />}
                                platform="Substack"
                                handle="@coralbaycatering"
                                hint="Recipes & island notes"
                                accent="from-sun-400 to-coral-500"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* NEWSLETTER */}
            <NewsletterSection />

            {/* CTA */}
            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <div className="aspect-[5/4] lg:aspect-square overflow-hidden rounded-sm">
                        <img
                            src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1800&q=90"
                            alt="Family style dinner"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div>
                        <span className="overline">Let's plan together</span>
                        <h2 className="font-serif text-4xl md:text-5xl text-forest-700 mt-5 leading-tight">
                            Custom menus tailored to <em>you and your guests.</em>
                        </h2>
                        <p className="text-forest-700/70 leading-relaxed mt-6 max-w-md">
                            Although we offer a wide variety of menu items, all items
                            are subject to last-minute changes due to availability of
                            product on a tiny island with extremely limited resources.
                            That's where the magic happens.
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/contact"
                                data-testid="cta-contact-btn"
                                className="inline-flex items-center gap-2 bg-forest-700 text-sand-100 px-7 py-3.5 rounded-sm hover:bg-forest-900 transition-colors text-sm tracking-wide"
                            >
                                Get in Touch
                            </Link>
                            <a
                                href="tel:3404236611"
                                data-testid="cta-call-btn"
                                className="inline-flex items-center gap-2 border border-forest-700 text-forest-700 px-7 py-3.5 rounded-sm hover:bg-forest-700 hover:text-sand-100 transition-colors text-sm tracking-wide"
                            >
                                <Phone className="w-4 h-4" strokeWidth={1.5} />
                                Call us
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

function SocialCard({ href, testid, icon, platform, handle, hint, accent = "from-forest-500 to-forest-700" }) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noreferrer"
            data-testid={testid}
            className="group relative bg-white border border-palm-400/30 rounded-sm p-6 hover:-translate-y-1 hover:border-transparent hover:shadow-xl transition-all duration-300 flex flex-col gap-3 overflow-hidden"
        >
            <div
                className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}
            />
            <div className="relative flex items-center justify-between">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${accent} text-white flex items-center justify-center group-hover:bg-white group-hover:bg-none group-hover:text-forest-700 transition-all`}>
                    {icon}
                </div>
                <ArrowRight
                    className="w-4 h-4 text-forest-700/40 group-hover:text-white group-hover:translate-x-0.5 transition-all"
                    strokeWidth={1.5}
                />
            </div>
            <div className="relative">
                <div className="overline group-hover:!text-white/90">{platform}</div>
                <div className="font-serif text-xl text-forest-700 mt-1 leading-tight group-hover:text-white">
                    {handle}
                </div>
                <div className="text-xs text-forest-700/60 mt-1 group-hover:text-white/85">
                    {hint}
                </div>
            </div>
        </a>
    );
}

